import { FormBlockInstance } from '../api-types';

export const MAGNITUDE_DIAL_TYPE = 'magnitude_dial';
export const MAGNITUDE_DIAL_TYPE_VERSION = 1;

/**
 * How a POSITION on the control maps to a value.
 *
 * `LOG` gives the low end as much travel as the high end, which is the only way a range spanning three
 * orders of magnitude is usable at all. It says nothing about which values are legal — that is `step` or
 * `snapValues`, and the two contracts are separate on purpose: collapsing them would let the
 * presentation quietly decide the domain, and two respondents on two screen widths could then reach
 * different sets of values for the same question.
 */
export type MagnitudeScale = 'LINEAR' | 'LOG';

export interface MagnitudeDialConfig {
  scale: MagnitudeScale;
  min: number;
  max: number;
  /** Exactly one of these is set. `step` is a regular grid; `snapValues` is an explicit list. */
  step: number | null;
  snapValues: number[] | null;
}

/** The answer shape pro-core validates. */
export interface MagnitudeDialAnswer {
  value: number;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asNumber = (value: unknown): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

/**
 * Read a stored config into the shape this renderer needs.
 *
 * `min` and `max` fall back to 0 and 100 rather than to null. pro-core requires both — a dial IS its
 * range and there is no way to draw one whose far end is unknown — so a config without them cannot have
 * been stored by the validator. Rendering an arbitrary but usable dial keeps one malformed question from
 * taking the whole form down, and the answer is refused on submit either way.
 */
export const readMagnitudeDialConfig = (
  block: FormBlockInstance
): MagnitudeDialConfig => {
  const config = asRecord(block.config);
  const interaction = asRecord(config.interaction);

  const min = asNumber(interaction.min) ?? 0;
  const max = asNumber(interaction.max) ?? 100;

  const snapValues = Array.isArray(interaction.snapValues)
    ? interaction.snapValues
        .map(asNumber)
        .filter((value): value is number => value !== null)
        .sort((a, b) => a - b)
    : null;

  return {
    // A log scale needs a positive minimum: zero has no position on one. pro-core refuses that pairing,
    // so seeing it here means the config is malformed, and a linear dial is the safe reading.
    scale: interaction.scale === 'LOG' && min > 0 ? 'LOG' : 'LINEAR',
    min,
    max: max > min ? max : min + 1,
    step: asNumber(interaction.step),
    snapValues: snapValues && snapValues.length > 0 ? snapValues : null
  };
};

export const readMagnitudeDialAnswer = (
  answer: unknown
): MagnitudeDialAnswer | null => {
  const value = asNumber(asRecord(answer).value);
  return value === null ? null : { value };
};

/**
 * The legal values, in order, when they can be enumerated.
 *
 * A slider moves over POSITIONS, and every position it can reach must be a value the server accepts —
 * otherwise the control produces answers that are refused at submit, which is the failure the whole
 * step/snap contract exists to prevent. So the dial is driven by an index into this list rather than by
 * the value itself.
 *
 * Returns null in three cases, and the third is the one worth reading:
 *
 *   1. No legality contract at all — nothing to enumerate.
 *   2. A step that would produce an unreasonable number of stops. A range of 50–50,000 stepping by 1
 *      is 49,951 positions: legal, and not something to build an array of.
 *   3. **A LOG scale driven by a step.** Enumerating an evenly-spaced step list and then indexing it
 *      gives every value the same travel, which is a linear dial wearing a log label — and since
 *      pro-core requires either a step or snap values, that would defeat the log scale on nearly every
 *      dial that declares one. Those take the continuous path below, which positions by ratio and
 *      lands on the step afterwards.
 *
 * Snap values keep their index even on a log scale: the list IS the author's spacing (10, 100, 1,000),
 * so equal travel per listed value is what a log scale wanted in the first place — and indexing is the
 * only way to guarantee every listed value stays reachable.
 */
const MAX_ENUMERATED = 1000;

export const legalValues = (config: MagnitudeDialConfig): number[] | null => {
  if (config.snapValues) {
    const inRange = config.snapValues.filter(
      value => value >= config.min && value <= config.max
    );
    /*
      Every listed value sitting outside the range leaves NOTHING legal, and an empty list is not a
      dial — it is a slider whose maximum index is -1, and a `nearest()` that reduces an empty array
      and throws. pro-core refuses that config, so seeing it means the definition is malformed; the
      same reading as a missing min or max applies, and for the same reason. An arbitrary but usable
      dial keeps one broken question from taking the whole form down, and the answer is refused on
      submit either way.
    */
    return inRange.length > 0 ? inRange : null;
  }
  if (config.step === null || config.step <= 0) {
    return null;
  }
  if (config.scale === 'LOG') {
    return null;
  }
  const count = Math.floor((config.max - config.min) / config.step) + 1;
  if (count < 2 || count > MAX_ENUMERATED) {
    return null;
  }
  return Array.from({ length: count }, (_, index) =>
    round(config.min + index * config.step!)
  );
};

/**
 * Position (0–1) to value, on whichever scale the question declared.
 *
 * Used only when the legal values cannot be enumerated. The result is rounded onto the step, so a
 * continuous-feeling drag still lands on a value the server accepts.
 */
export const valueAt = (
  position: number,
  config: MagnitudeDialConfig
): number => {
  const clamped = Math.min(1, Math.max(0, position));
  const raw =
    config.scale === 'LOG'
      ? Math.exp(
          Math.log(config.min) +
            clamped * (Math.log(config.max) - Math.log(config.min))
        )
      : config.min + clamped * (config.max - config.min);

  if (config.step && config.step > 0) {
    const steps = Math.round((raw - config.min) / config.step);
    return clamp(round(config.min + steps * config.step), config);
  }
  return clamp(round(raw), config);
};

/**
 * Value to position (0–1), the inverse of `valueAt`, for drawing where the handle sits.
 *
 * Clamped at the end, which `valueAt` already does to its own input and this did not. The asymmetry
 * was the defect: a stored answer outside the range produced a position outside 0–1, and on a LOG
 * scale a stored zero produced `-Infinity` and a negative produced `NaN` — each of which reaches the
 * slider as its `value` and breaks the control rather than merely misplacing the handle.
 *
 * A resumed answer is the way that happens. Nothing here can vouch for a number it did not compute,
 * so the handle goes to the nearest end it can draw instead of nowhere at all.
 */
export const positionOf = (
  value: number,
  config: MagnitudeDialConfig
): number => {
  const ratio = (): number => {
    if (config.scale === 'LOG') {
      // Guarded rather than clamped afterwards: `Math.log` of zero is `-Infinity` and of a negative
      // is `NaN`, and neither survives arithmetic to reach the clamp below as a number.
      if (value <= 0) {
        return 0;
      }
      const span = Math.log(config.max) - Math.log(config.min);
      return span === 0 ? 0 : (Math.log(value) - Math.log(config.min)) / span;
    }
    const span = config.max - config.min;
    return span === 0 ? 0 : (value - config.min) / span;
  };

  return Math.min(1, Math.max(0, ratio()));
};

const clamp = (value: number, config: MagnitudeDialConfig): number =>
  Math.min(config.max, Math.max(config.min, value));

/**
 * Four decimal places, matching pro-core's `MAX_SCALE`.
 *
 * A log scale produces long floats and the server refuses anything finer, so rounding here is what
 * keeps a legitimate drag from being rejected on submit.
 */
const round = (value: number): number => Number(value.toFixed(4));

/** Whether an answer is complete enough to move on. Any stored number is; the server checks the rest. */
export const isMagnitudeDialAnswered = (answer: unknown): boolean =>
  readMagnitudeDialAnswer(answer) !== null;
